import React, { useState } from 'react';
import { Alert, Button, Card, Radio, Space, Spin, Table, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { PageHeader } from '@shared/components/PageHeader';
import {
  ClaudeUsageDayRow,
  useClaudeModelConfig,
  useClaudeUsage,
  useUpdateClaudeModelConfig,
} from '@shared/api/hooks/useClaudeModelConfig';

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Impostazioni AI — modello Claude per l'estrazione Vision degli scontrini +
 * usage stimato. Ruolo DEVELOPER only (PLAN_CLAUDE_MODEL_SETTINGS.md §5):
 * nav item nascosto per CLUB_ADMIN, backend rifiuta comunque con 403.
 *
 * Dropdown vincolato ai modelli in whitelist (mai testo libero) — vedi
 * backend/src/modules/receipts/claude-recommended-models.ts.
 */
export function AiSettingsPage(): React.ReactElement {
  const { data: config, isLoading, refetch, isFetching } = useClaudeModelConfig();
  const { data: usage, isLoading: usageLoading } = useClaudeUsage(30);
  const updateMutation = useUpdateClaudeModelConfig();
  const [draft, setDraft] = useState<string | null>(null);

  const selected = draft ?? config?.activeModel ?? '';
  const dirty = config != null && selected !== config.activeModel;

  const onSave = async (): Promise<void> => {
    if (!selected) return;
    try {
      await updateMutation.mutateAsync(selected);
      setDraft(null);
      message.success('Modello Claude aggiornato — attivo entro 60s (cache in-process).');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore salvataggio'));
    }
  };

  if (isLoading || !config) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  const usageColumns = [
    {
      title: 'Giorno',
      dataIndex: 'date',
      key: 'date',
      render: (v: string) => dayjs(v).format('DD/MM/YYYY'),
    },
    { title: 'Modello', dataIndex: 'model', key: 'model' },
    { title: 'Richieste', dataIndex: 'requests', key: 'requests' },
    {
      title: 'Token input',
      dataIndex: 'inputTokens',
      key: 'inputTokens',
      render: (v: number) => v.toLocaleString('it-IT'),
    },
    {
      title: 'Token output',
      dataIndex: 'outputTokens',
      key: 'outputTokens',
      render: (v: number) => v.toLocaleString('it-IT'),
    },
    {
      title: 'Costo stimato',
      dataIndex: 'costEstimateCents',
      key: 'costEstimateCents',
      render: (v: number) => formatCents(v),
    },
  ];

  return (
    <>
      <PageHeader
        title="Impostazioni AI"
        subtitle="Modello Claude per l'estrazione Vision degli scontrini, check giornaliero di validità e usage stimato."
        extra={
          <>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
              Ricarica
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={onSave}
              disabled={!dirty}
              loading={updateMutation.isPending}
            >
              Salva
            </Button>
          </>
        }
      />

      <Card title="Modello attivo" style={{ marginBottom: 16 }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Limite del check giornaliero"
          description="L'Anthropic Models API espone solo se un modello è valido oggi, non una data di ritiro futura — il check rileva un modello già morto entro 24h, non è un vero preavviso anticipato."
        />
        <Radio.Group
          value={selected}
          onChange={(e) => setDraft(e.target.value as string)}
          style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}
        >
          {config.options.map((opt) => (
            <Radio key={opt.id} value={opt.id} style={{ width: '100%' }}>
              <Space direction="vertical" size={0} style={{ marginLeft: 4 }}>
                <Space>
                  <Typography.Text strong>{opt.displayName}</Typography.Text>
                  <Typography.Text type="secondary" code>
                    {opt.id}
                  </Typography.Text>
                  {opt.valid ? (
                    <Tag color="green">Valido oggi</Tag>
                  ) : (
                    <Tag color="red">Non valido — {opt.validityError ?? 'errore sconosciuto'}</Tag>
                  )}
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {opt.note}
                </Typography.Text>
              </Space>
            </Radio>
          ))}
        </Radio.Group>
        <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0, fontSize: 12 }}>
          {config.updatedAt
            ? `Ultima modifica: ${new Date(config.updatedAt).toLocaleString('it-IT')}`
            : 'Nessuna modifica manuale — in uso il fallback (env o default di codice).'}
        </Typography.Paragraph>
      </Card>

      <Card
        title="Usage stimato (ultimi 30 giorni)"
        extra={
          usage && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Prezzi aggiornati al {usage.pricesUpdatedAt} — stima, non fatturazione reale
            </Typography.Text>
          )
        }
      >
        {usageLoading || !usage ? (
          <Spin />
        ) : (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`Costo totale stimato: ${formatCents(usage.totalCostEstimateCents)}`}
              description="Calcolato da un listino prezzi mantenuto a mano nel codice (claude-pricing.ts), non dalla fatturazione Anthropic reale."
            />
            <Table<ClaudeUsageDayRow>
              size="small"
              rowKey={(r) => `${r.date}-${r.model}`}
              columns={usageColumns}
              dataSource={usage.rows}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'Nessun utilizzo registrato nel periodo.' }}
            />
          </>
        )}
      </Card>
    </>
  );
}
